import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import {
    Backdrop,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    styled,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {
    common,
    formatPhoneValue,
    GenericObject,
    getApplicationId,
    getIsPortal,
    getIsWebsite,
    ScreenProps,
} from '@fnol/library/src/lib';
import { ContactVerification, StepperControls, StepWrapper, StyledCardSection } from '@fnol/library/src/components';
import { useApi } from '@fnol/library/src/hooks';
import { constValues, insuranceGroup, legalPersonType, translations } from '../../lib/consts';
import { checkContactData, getClaimConfirmation, getClaimConfirmationAnonymous } from '../../rpc/actions';
import { scrollToFirstError } from '../../lib/helpers';
import SuccessScreen from '../SuccessScreen';
import UserNotificationScreen from '../UserNotificationScreen';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

let data = new FormData();

const ConfirmationScreen = ({ navigator }: ScreenProps) => {
    const { values, setErrors, setTouched, setFieldValue } = useFormikContext<any>();
    const [currentStep, { goBack }] = navigator;
    const [contactVerified, setContactVerified] = useState<GenericObject>({});

    const hideStepper = () => {
        const allWithClass = Array.from(document.getElementsByClassName('MuiStepper-root'));

        allWithClass.forEach((element: Element) => {
            if (element instanceof HTMLElement) {
                element.style.display = 'none';
            }
        });
    };

    const { response: claimConfirmation, fetchMethod: fetchClaimConfirmation } = useApi(
        () => getClaimConfirmation(data),
        hideStepper,
    );

    const { response: claimConfirmationAnonymous, fetchMethod: fetchClaimConfirmationAnonymous } = useApi(
        () => getClaimConfirmationAnonymous(data),
        hideStepper,
    );

    const onCheckBoxChange = (e: any) => {
        setFieldValue('contactAgreement', e.target.checked);
    };

    const { response: contactData, fetchMethod: fetchContactData } = useApi(
        () => checkContactData(values.tempID),
        undefined,
    );

    useEffect(() => {
        if (values.tempID) {
            fetchContactData();
        }
    }, []);

    const contactVerificationPayload = contactData?.data?.data ?? {};
    const showOTP = Boolean(contactVerificationPayload?.prikaziOTP);
    const shouldVerifyEmail = Boolean(contactVerificationPayload?.verifikujEmail);
    const shouldVerifyPhone = Boolean(contactVerificationPayload?.verifikujMobilniTelefon);
    const shouldRenderContactVerification = values.tempID && (shouldVerifyEmail || shouldVerifyPhone);
    const isContactVerificationPending = Boolean(
        (values.tempID && !contactData.isSuccess) ||
            (shouldRenderContactVerification && !contactVerified.phoneGuid && !contactVerified.emailGuid),
    );

    const handleSubmit = (): void => {
        if (isContactVerificationPending) {
            return;
        }

        if (!claimConfirmation.isLoading && !claimConfirmationAnonymous.isLoading) {
            data = new FormData();
            data.append('stetaTempId', values.tempID);
            data.append('saglasnost', values.contactAgreement);

            const existingErrors: GenericObject = {};

            if (showOTP) {
                setTouched(
                    {
                        oneTimePass: true,
                    },
                    false,
                );

                if (values.oneTimePass === '' || values.oneTimePass === null) {
                    existingErrors.oneTimePass = translations.validationMessages.REQUIRED_FIELD;
                    setErrors(existingErrors);
                    scrollToFirstError(existingErrors);
                } else {
                    data.append('kontakt', values.tempPassContact);
                    data.append('sifra', values.oneTimePass);
                    fetchClaimConfirmationAnonymous();
                }
            } else if (shouldVerifyEmail || shouldVerifyPhone) {
                if (!contactVerified.phoneGuid && !contactVerified.emailGuid) {
                    if (!contactVerified.phoneGuid) {
                        existingErrors.submitterIndividualPhone = translations.validationMessages.REQUIRED_FIELD;
                    }
                    if (!contactVerified.emailGuid) {
                        existingErrors.submitterIndividualEmail = translations.validationMessages.REQUIRED_FIELD;
                    }
                    setErrors(existingErrors);
                } else {
                    if (shouldVerifyEmail) {
                        data.append('guid', contactVerified.emailGuid);
                        data.append('kontakt', values.submitterIndividualEmail);
                    }
                    if (shouldVerifyPhone) {
                        data.append('guid', contactVerified.phoneGuid);
                        data.append('kontakt', values.submitterIndividualPhone);
                    }
                    if (getIsWebsite(getApplicationId())) {
                        fetchClaimConfirmationAnonymous();
                    } else {
                        fetchClaimConfirmation();
                    }
                }
            } else {
                if (getIsWebsite(getApplicationId())) {
                    fetchClaimConfirmationAnonymous();
                } else {
                    fetchClaimConfirmation();
                }
            }
        }
    };

    const handleGoBack = (): void => {
        goBack();
    };

    const toDate = (date: string) => {
        return new Date(date);
    };

    const updDate = `${('0' + toDate(values.dateOfOccurance).getDate()).slice(-2)}.${(
        '0' +
        (toDate(values.dateOfOccurance).getMonth() + 1)
    ).slice(-2)}.${toDate(values.dateOfOccurance).getFullYear()}`;

    function createData(title: string, data: string) {
        return { title, data };
    }

    const submitter =
        values.submitterType === legalPersonType.individual
            ? `${values.submitterIndividualName} ${values.submitterIndividualLastName}`
            : values.submitterType === legalPersonType.legal
            ? `${values.submitterLegalEntityContactName} ${values.submitterLegalEntityContactLastName}`
            : '';
    const submitterContact =
        values.submitterType === legalPersonType.individual
            ? `${formatPhoneValue(values.submitterIndividualPhone)}${
                  formatPhoneValue(values.submitterIndividualPhone) && values.submitterIndividualEmail ? ', ' : ''
              }${values.submitterIndividualEmail}`
            : values.submitterType === legalPersonType.legal
            ? `${formatPhoneValue(values.submitterLegalEntityContactPhone)}${
                  formatPhoneValue(values.submitterLegalEntityContactPhone) && values.submitterLegalEntityContactEmail
                      ? ', '
                      : ''
              }${values.submitterLegalEntityContactEmail}`
            : '';

    useEffect(() => {
        if (!getIsPortal(getApplicationId()) && values.groupInsuranceMethod !== insuranceGroup.policyNumber) {
            if (values.addressObjectSameAsSubmitter === 'false') {
                setFieldValue('damagePlaceAndAddress', `${values.objectCity.label}, ${values.objectStreet.label}`);
            }
            if (
                values.addressObjectSameAsSubmitter === 'true' &&
                (values.submitterLegalEntityCountry === constValues.countrySerbiaId.toString(10) ||
                    values.submitterLegalEntityCountry === constValues.countrySerbiaId)
            ) {
                if (values.submitterType === legalPersonType.individual) {
                    setFieldValue(
                        'damagePlaceAndAddress',
                        `${values.submitterIndividualCityInSerbia.label}, ${values.submitterIndividualAddress.label} ${values.submitterIndividualAddressNumber}`,
                    );
                }
                if (values.submitterType === legalPersonType.legal) {
                    setFieldValue(
                        'damagePlaceAndAddress',
                        `${values.submitterLegalEntityCityInSerbia.label}, ${values.submitterLegalEntityAddress.label} ${values.submitterLegalEntityAddressNumber}`,
                    );
                }
            }
        }

        if (getIsPortal(getApplicationId())) {
            setFieldValue('policyNumberLabel', translations.POLICY_NUMBER);
            setFieldValue('policyNumberValue', values.policyNumberAPI);
        } else {
            if (values.groupInsuranceMethod === insuranceGroup.mobileOrWebApp) {
                setFieldValue('policyNumberLabel', translations.INSURANCE_CONFIRMATION_ID);
                setFieldValue('policyNumberValue', values.insuranceConfirmationID);
            } else if (values.groupInsuranceMethod === insuranceGroup.publicUtility) {
                setFieldValue('policyNumberLabel', translations.PUBLIC_UTILITY_LABEL);
                const utility = values.publicUtilities
                    ?.filter((utility: GenericObject) => {
                        return utility.id === values.publicUtility;
                    })
                    .map((utility: GenericObject) => {
                        return utility.value;
                    });
                setFieldValue('policyNumberValue', utility);
            } else if (values.groupInsuranceMethod === insuranceGroup.union) {
                setFieldValue('policyNumberLabel', translations.UNION_LABEL);
                const union = values.unions
                    ?.filter((union: GenericObject) => {
                        return union.id === values.union;
                    })
                    .map((union: GenericObject) => {
                        return union.value;
                    });
                setFieldValue('policyNumberValue', union);
            } else if (values.groupInsuranceMethod === insuranceGroup.directMail) {
                setFieldValue('policyNumberLabel', translations.REFERENCE_NUMBER);
                setFieldValue('policyNumberValue', values.referenceNumber);
            } else {
                setFieldValue('policyNumberLabel', translations.POLICY_NUMBER);
                setFieldValue('policyNumberValue', values.policyNumber);
            }
        }
    }, [values.groupInsuranceMethod]);

    const shouldSendLinkLabel = values.objectHasSmsLink === 'true' ? translations.YES : translations.NO;

    const rows = [
        createData(values.policyNumberLabel, values.policyNumberValue),
        createData(translations.confirmationScreen.DATE, updDate),
        createData(translations.confirmationScreen.POLICY_SUBMITTER, submitter),
        createData(translations.confirmationScreen.POLICY_SUBMITTER_CONTACT, submitterContact),
        createData(translations.CAUSE_TYPE, `${values.causeName}`),
        createData(translations.confirmationScreen.SEND_LINK, shouldSendLinkLabel),
    ];

    if (values.damagePlaceAndAddress) {
        rows.push(
            createData(translations.confirmationScreen.DAMAGE_PLACE_AND_ADDRESS, `${values.damagePlaceAndAddress}`),
        );
    }

    return (
        <StepWrapper>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={claimConfirmation.isLoading || claimConfirmationAnonymous.isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <StyledCardSection>
                {!claimConfirmation.isSuccess && !claimConfirmationAnonymous.isSuccess ? (
                    <>
                        <TableContainer sx={{ paddingBottom: 7 }}>
                            <Table sx={{ minWidth: 230 }} aria-label="customized table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>
                                            {translations.confirmationScreen.BASIC_INFORMATION}
                                        </StyledTableCell>
                                        <StyledTableCell align="left"></StyledTableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {rows.map((row) => (
                                        <StyledTableRow key={row.title}>
                                            <StyledTableCell component="th" scope="row">
                                                {row.title}
                                            </StyledTableCell>
                                            <StyledTableCell align="left">{row.data}</StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <FormControlLabel
                            name="contactAgreement"
                            id="contactAgreement"
                            control={<Checkbox />}
                            onChange={onCheckBoxChange}
                            label={
                                <Typography style={{ fontSize: '13px' }}>
                                    {translations.confirmationScreen.AGREEMENT}
                                </Typography>
                            }
                            componentsProps={{ typography: { fontSize: '13px' } }}
                        />

                        {getIsWebsite(getApplicationId()) && showOTP && (
                            <UserNotificationScreen tempId={values.tempID} />
                        )}
                        {shouldRenderContactVerification ? (
                            <ContactVerification
                                ucn={values.submitterIndividualUCN}
                                emailShouldVerify={shouldVerifyEmail}
                                phoneShouldVerify={shouldVerifyPhone}
                                setContactVerified={setContactVerified}
                                phoneFieldName="submitterIndividualPhone"
                                emailFieldName="submitterIndividualEmail"
                            />
                        ) : null}
                    </>
                ) : (
                    <SuccessScreen
                        claimNo={
                            claimConfirmation.data.data
                                ? claimConfirmation.data.data
                                : claimConfirmationAnonymous.data.data
                        }
                    />
                )}

                {claimConfirmationAnonymous.error.message && !claimConfirmationAnonymous.isSuccess && (
                    <Typography className="confirmation-message">
                        {claimConfirmationAnonymous.error.response?.data?.match(/^\([0-9]+\)/)
                            ? translations.confirmationScreen.WRONG_PASS
                            : translations.confirmationScreen.CLAIM_ERROR}
                    </Typography>
                )}
                {claimConfirmation.error.message && !claimConfirmation.isSuccess && (
                    <Typography className="confirmation-message">
                        {claimConfirmation.error.response?.data?.match(/^\([0-9]+\)/)
                            ? translations.confirmationScreen.WRONG_PASS
                            : translations.confirmationScreen.CLAIM_ERROR}
                    </Typography>
                )}
            </StyledCardSection>

            <>
                {!claimConfirmation.isSuccess && !claimConfirmationAnonymous.isSuccess ? (
                    <StepperControls
                        onPrevious={handleGoBack}
                        onNext={handleSubmit}
                        onNextLabel={common.SEND}
                        disableNext={isContactVerificationPending}
                        idNext="confirmationScreenNext"
                        idPrevious="confirmationScreenPrevious"
                    />
                ) : null}
            </>
        </StepWrapper>
    );
};

export default ConfirmationScreen;
